import React from "react";
import styled from "styled-components";

interface Step4RoundOptionsProps {
  PlayTypeBoxGroup: React.ComponentType<any>;
  TeeTypeBoxGroup: React.ComponentType<any>;
  EtcRequirementsBoxGroup: React.ComponentType<any>;
  AwardsBoxGroup: React.ComponentType<any>;
  PricePerPersonBoxGroup: React.ComponentType<any>;
  activePlayTypeIds?: number[];
  activeTeeTypeIds?: number[];
  activeEtcRequirementsIds?: number[];
  activeAwardsIds?: number[];
  activePricePerPersonIds?: number[];
  onCancel?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

export default function Step4RoundOptions({
  PlayTypeBoxGroup,
  TeeTypeBoxGroup,
  EtcRequirementsBoxGroup,
  AwardsBoxGroup,
  PricePerPersonBoxGroup,
  activePlayTypeIds,
  activeTeeTypeIds,
  activePricePerPersonIds,
  onCancel: _onCancel,
  onNext,
  onPrevious,
}: Step4RoundOptionsProps) {
  return (
    <RoundOptionsContainer>
      {/* Main Content */}
      <MainContent>
        <PageTitle>라운드 옵션을 선택해주세요.</PageTitle>

        {/* Game Style Section */}
        <FormSection>
          <SectionTitle>
            <Required>* </Required>
            <span>경기 방식</span>
          </SectionTitle>
          <PlayTypeBoxGroup />
        </FormSection>

        {/* Tee Usage Section */}
        <FormSection>
          <SectionTitle>
            <Required>* </Required>
            <span>티 사용</span>
          </SectionTitle>
          <TeeTypeBoxGroup />
        </FormSection>

        {/* Price Per Person Section */}
        <FormSection>
          <SectionTitle>
            <Required>* </Required>
            <span>1인 객단가</span>
            <Subtitle>(* 연단체 우선순위 배정의 기준)</Subtitle>
          </SectionTitle>
          <PricePerPersonBoxGroup />
        </FormSection>

        {/* Request Section */}
        <FormSection>
          <SectionTitle>
            <span>요청 사항</span>
          </SectionTitle>
          <EtcRequirementsBoxGroup />
        </FormSection>

        {/* Award Request Section */}
        <FormSection>
          <SectionTitle>
            <span>시상 요청</span>
          </SectionTitle>
          <AwardsBoxGroup />
        </FormSection>
      </MainContent>

      {/* Bottom CTA */}
      <BottomCta>
        <ButtonGroup>
          <PreviousButton onClick={onPrevious}>뒤로</PreviousButton>
          <CtaButton
            onClick={onNext}
            disabled={
              !activePlayTypeIds ||
              activePlayTypeIds.length === 0 ||
              !activeTeeTypeIds ||
              activeTeeTypeIds.length === 0 ||
              !activePricePerPersonIds ||
              activePricePerPersonIds.length === 0
            }
          >
            다음
          </CtaButton>
        </ButtonGroup>
      </BottomCta>
    </RoundOptionsContainer>
  );
}

const RoundOptionsContainer = styled.div`
  min-height: 100vh;
  padding: 16px;
  width: 100%;
  background: #fdfdfd;
  position: relative;

  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  padding-bottom: 124px; /* Bottom CTA(108px) + 여유 */
`;

const MainContent = styled.div`
  min-height: calc(100vh - 108px);
  top: 10px;
  width: 100%;
`;

const PageTitle = styled.h1`
  color: #1a1a1a;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  margin: 8px 0 24px 0;
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.div`
  color: #1a1a1a;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Required = styled.span`
  color: #f97000;
  font-weight: 700;
`;

const Subtitle = styled.span`
  color: #79818a;
  font-size: 12px;
  font-weight: 400;
  margin-left: 8px;
`;

const BottomCta = styled.div`
  display: flex;
  width: 100%;
  max-width: 800px;
  height: 108px;
  padding: 26px 16px;
  justify-content: center;
  align-items: center;
  background: linear-gradient(0deg, #fdfdfd 76.92%, rgba(253, 253, 253, 0) 100%);
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  margin: 0 auto;
`;

const PreviousButton = styled.button`
  display: flex;
  width: 100%;
  max-width: 400px;
  min-width: 170px;
  height: 60px;
  padding: 19px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 10px;
  background: white;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &:active {
    background-color: #e5e5e5;
  }
`;

const CtaButton = styled.button`
  display: flex;
  width: 100%;
  max-width: 400px;
  min-width: 170px;
  height: 60px;
  padding: 19px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 10px;
  background: var(--Teeshot-Primary-Color-Teeshot-Green, #004b49);
  color: white;
  border: none;
  font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #003a38;
  }

  &:active {
    background-color: #002a28;
  }

  &:disabled {
    background-color: #d1d5d8;
    color: #79818a;
    cursor: not-allowed;
  }
`;
